/** «Tronson nou»: alegi categoria, apoi de unde pornește desenul.
 *
 *  DE CE E UN ECRAN ȘI NU UN BUTON. Ce trebuie aflat de la om e UN singur
 *  lucru — unde poate sta modulul pe bară —, dar răspunsul hotărăște forma
 *  amprentei, iar o formă se alege uitându-te la ea. De-aia cele trei categorii
 *  se arată desenate, la scara lor reală una față de alta: colțul chiar e cât
 *  două module de bară puse în unghi, și asta se vede.
 *
 *  CODUL SE CERE TOT AICI, fiindcă în catalog un modul E codul lui: «M9 v1»
 *  numește un document și numai unul, pe toată platforma. Unicitatea o
 *  hotărăște serverul (409 la un cod luat), aici se verifică doar forma lui, ca
 *  omul să nu trimită degeaba.
 *
 *  AL DOILEA PAS E DE UNDE PORNEȘTE DESENUL, și e un pas separat fiindcă e altă
 *  întrebare: prima e despre ce E modulul (unde stă pe bară — forma lui), a
 *  doua despre cum se ajunge la desenul lui. Foaia goală era, până acum,
 *  singurul răspuns, și era răspunsul prost în cele mai multe cazuri: cine face
 *  al patrulea modul de mijloc al proiectului are deja trei desenate, iar cine
 *  vine cu un plan de la un arhitect îl are pe hârtie.
 *
 *  SE NAȘTE CIORNĂ, ca orice altceva salvat din editor: probele catalogului pică
 *  pe un modul fără nicio cameră — n-are arie utilă și nu acoperă nimic din
 *  amprentă —, iar asta e chiar starea lui adevărată. Nicio generare nu-l vede
 *  până nu-l publică cineva.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import * as api from "../../api";
import type { Tronson } from "../../api";
import { anvelopa } from "./doc";
import { ePdf, imagineDinPdf } from "./pdf";
import {
  decupeaza, imagineDinFisier, originalDinFisier, pasulCitirii,
  procentCitire,
} from "./calc";
import DecupajImagine from "./DecupajImagine";
import { rolulCategoriei, tronsonNou, type CategorieTronson }
  from "./editor/model";
import { useT } from "../../i18n";
import type { Cheie } from "../../i18n";

/** NUMELE ȘI PROPOZIȚIA FIECĂREI CATEGORII SUNT ALE COLECȚIEI, nu scrise a doua
 *  oară aici: e același lucru spus în același loc, iar ecranul ăsta e chiar
 *  poarta de intrare în ea. */
const CATEGORII: readonly {
  cheie: CategorieTronson; titlu: Cheie; sub: Cheie;
}[] = [
  { cheie: "capat", titlu: "colectie.capat", sub: "colectie.capat_sub" },
  { cheie: "mijloc", titlu: "colectie.mijloc", sub: "colectie.mijloc_sub" },
  { cheie: "colt", titlu: "colectie.colt", sub: "colectie.colt_sub" },
  { cheie: "izolat", titlu: "colectie.izolat", sub: "colectie.izolat_sub" },
];

/** De unde pornește desenul. */
type Pornire = "gol" | "existent" | "imagine";

const PORNIRI: readonly { cheie: Pornire; titlu: Cheie; sub: Cheie }[] = [
  { cheie: "gol", titlu: "nou.gol", sub: "nou.gol_sub" },
  { cheie: "existent", titlu: "nou.existent", sub: "nou.existent_sub" },
  { cheie: "imagine", titlu: "nou.imagine", sub: "nou.imagine_sub" },
];

/** Amprenta categoriei, desenată. CITITĂ DIN CHIAR DOCUMENTUL CARE SE VA NAȘTE
 *  (`tronsonNou` → `anvelopa`), nu desenată a doua oară: o schiță scrisă de mână
 *  ar fi rămas în urmă la prima schimbare a formei tipice, iar omul ar fi ales
 *  uitându-se la altceva decât primește.
 *
 *  SCARA E COMUNĂ tuturor celor trei, ca să se poată compara: colțul e de două
 *  ori și jumătate mai lat decât un modul de bară, și tocmai asta e informația.
 */
function Amprenta({ categorie, scara }: {
  categorie: CategorieTronson; scara: number;
}) {
  const inel = useMemo(() => anvelopa(tronsonNou("X", categorie)), [categorie]);
  const xs = inel.map((q) => q[0]);
  const ys = inel.map((q) => q[1]);
  const x0 = Math.min(...xs);
  const y0 = Math.min(...ys);
  const w = Math.max(...xs) - x0;
  const h = Math.max(...ys) - y0;
  const M = 1;                                        // rama, în metri
  return (
    <svg className="ob-tr-nou-schita"
         width={(w + 2 * M) * scara} height={(h + 2 * M) * scara}
         viewBox={`${x0 - M} ${y0 - M} ${w + 2 * M} ${h + 2 * M}`}
         aria-hidden="true">
      <polygon points={inel.map((q) => `${q[0]},${q[1]}`).join(" ")}
               vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

export default function TronsonNou({
  projectId, laInchidere, laCreat, laPornit,
}: {
  /** Gol = colecția OMULUI (migrarea 0045). Ecranul e același; se schimbă
   *  doar rutele pe care pleacă modulul nou. */
  projectId: string;
  laInchidere: () => void;
  /** Codul modulului scris. Ecranul care a cerut fereastra hotărăște ce
   *  urmează — colecția se reîncarcă, editorul se mută pe el. */
  laCreat: (cod: string) => void;
  /** CITIREA A PORNIT, și atât. Nu e același lucru cu `laCreat`: modulul încă
   *  nu există, iar ecranul nu trebuie să plece spre un editor gol. Pagina
   *  colecției își pune o bară de progres pe codul ăsta și merge mai departe
   *  (13.10.2026: «the upload should happen in the background... we should be
   *  able to refresh the page, do other things, maybe upload other tronsoane»). */
  laPornit: (cod: string, cerere: Promise<void>) => void;
}) {
  const t = useT();
  const [pas, setPas] = useState<"forma" | "pornire">("forma");
  const [categorie, setCategorie] = useState<CategorieTronson>("mijloc");
  const [cod, setCod] = useState("");
  const [pornire, setPornire] = useState<Pornire>("gol");
  const [lucrez, setLucrez] = useState(false);
  const [eroare, setEroare] = useState("");
  /** Citirea planșei: când a pornit (ceasul ecranului) și starea serverului.
   *  Bara se mișcă pe ceasul ăsta, nu pe răspunsurile la trei secunde. */
  const [citire, setCitire] = useState<
    { pornit: number; stare?: string; etapa?: api.ImportImagine["etapa"] } | null>(null);
  const [acum, setAcum] = useState(() => Date.now());
  useEffect(() => {
    if (!citire) return;
    const ceas = setInterval(() => setAcum(Date.now()), 500);
    return () => clearInterval(ceas);
  }, [citire]);
  const viu = useRef(true);
  useEffect(() => () => { viu.current = false; }, []);

  /** Modulele din care se poate copia — cele din ACEEAȘI categorie. Un desen de
   *  colț pus într-un modul de mijloc n-ar fi un punct de plecare, ci o formă
   *  greșită de reparat, și e mai puțin de lucru de la zero. */
  const [module, setModule] = useState<Tronson[] | null>(null);
  const [dinCod, setDinCod] = useState<string | null>(null);

  /** Imaginea de trasat: se ține în starea ecranului până la naștere, apoi trece
   *  în calcul modulului (`calc.ts`) — de-acolo o ia editorul. */
  const [imagine, setImagine] = useState<
    { url: string; latime: number; inaltime: number } | null>(null);
  /** Fișierul ÎNTREG, pentru citire — v. `originalDinFisier`. */
  const [original, setOriginal] = useState<string | null>(null);
  const alegeFisier = useRef<HTMLInputElement | null>(null);
  /** Fișierul ales, cum a venit — din el se decupează (și a doua oară). */
  const [fisier, setFisier] = useState<File | null>(null);
  /** Ecranul de decupare e deschis. */
  const [decupez, setDecupez] = useState(false);
  /** Cât ține randarea paginii de PDF. O planșă mare ia o clipă, iar fără
   *  semn ecranul pare că n-a primit fișierul și omul îl alege din nou. */
  const [randez, setRandez] = useState(false);

  /** Forma codului, verificată aici doar ca să nu se trimită degeaba — cea care
   *  contează e a serverului (v. `api/catalog.py::salveaza_ca_nou`). */
  /** CODUL POATE AVEA SPAȚII. Era `[A-Z][A-Z0-9_-]{0,15}`, deci «TEST COTE» nu
   *  trecea și butonul rămânea stins fără să spună de ce — oamenii scriu acolo
   *  un TITLU, nu o siglă (01.10.2026). Spațiile dinafară se taie, iar cele
   *  dinăuntru se strâng la unul singur: «M9  B» și «M9 B» sunt același modul,
   *  iar două rânduri care arată la fel și nu sunt ar fi mai rău decât un câmp
   *  strict. */
  const codOk = api.NUME_MODUL.test(api.numeCurat(cod));

  // Colecția se cere O DATĂ, la intrarea în pasul al doilea, nu la deschiderea
  // ferestrei: cine face un modul gol — cazul cel mai des — n-are nevoie de ea,
  // iar cererea ar fi plecat oricum.
  useEffect(() => {
    if (pas !== "pornire" || module !== null) return;
    let viu = true;
    void (projectId ? api.listTronsoane(projectId) : api.listTronsoaneleMele())
      .then((c) => { if (viu) setModule(c.modules); })
      .catch(() => { if (viu) setModule([]); });
    return () => { viu = false; };
  }, [pas, module, projectId]);

  const deCopiat = (module ?? []).filter((m) => m.category === categorie);

  /** Fișierul ales merge ÎNTÂI la decupare; calcul și originalul se fac din ce
   *  rămâne după ea, ca poza de sub desen să fie aceeași cu cea citită. */
  const alegeImaginea = (f: File | null | undefined) => {
    if (!f) return;
    // UN PDF SE RANDEAZĂ ÎNTÂI, apoi intră în flux ca orice poză: decuparea,
    // calculul și originalul lucrează pe imaginea ieșită din el, deci nimic în
    // aval nu știe că a fost PDF (v. `pdf::imagineDinPdf`). Cerut pe
    // 19.10.2026 — planurile de tronson vin și ca PDF, nu doar ca poză.
    if (ePdf(f)) {
      setEroare("");
      setRandez(true);
      void imagineDinPdf(f)
        .then((poza) => { setFisier(poza); setDecupez(true); })
        .catch(() => setEroare(t("nou.pdf_necitit")))
        .finally(() => setRandez(false));
      return;
    }
    if (!f.type.startsWith("image/")) { setEroare(t("nou.imagine_lipsa")); return; }
    setEroare("");
    setFisier(f);
    setDecupez(true);
  };

  const iaDecupajul = (d: Parameters<typeof decupeaza>[1]) => {
    if (!fisier) return;
    setDecupez(false);
    void decupeaza(fisier, d)
      .then((f) => Promise.all([imagineDinFisier(f), originalDinFisier(f)]))
      .then(([calc, intreg]) => { setImagine(calc); setOriginal(intreg); })
      .catch(() => setEroare(t("nou.imagine_lipsa")));
  };

  const creeaza = async () => {
    const cerut = api.numeCurat(cod);
    if (!codOk || lucrez) return;
    if (pornire === "existent" && !dinCod) { setEroare(t("nou.modul_lipsa")); return; }
    if (pornire === "imagine" && !imagine) { setEroare(t("nou.imagine_lipsa")); return; }
    setLucrez(true);
    setEroare("");
    if (pornire === "imagine") { setCitire({ pornit: Date.now() }); setAcum(Date.now()); }
    try {
      // DIN IMAGINE: modulul e DESENAT de lanțul de import, pe server — nu se
      // naște gol și nu se cere nimănui să-l deseneze după. Ține minute: pe
      // server, un cititor se uită la planșă decupaj cu decupaj.
      if (pornire === "imagine" && imagine) {
        // CATEGORIA MERGE CU IMAGINEA. Pe drumul «gol» ea intră în document
        // prin `tronsonNou(cerut, categorie)`; pe drumul «imagine» nu pleca
        // deloc, iar importatorul scria `role: "standalone"` pentru orice
        // planșă — de-aia colțul ales în ecran nu se mai regăsea pe modul.
        // HÂRTIA DE CALC PLEACĂ ODATĂ CU PLANȘA. Serverul o pune pe modul
        // în clipa în care îl scrie, gata calibrată: el știe unde a căzut
        // fiecare pixel al planșei, deci poza stă exact sub zidurile care au
        // ieșit din ea. Scrisă de-aici, s-ar fi pierdut — fereastra pleacă.
        const calc: api.CalcDeImport = {
          imagine: imagine.url, latime: imagine.latime, inaltime: imagine.inaltime,
        };
        // FEREASTRA PLEACĂ PE CLIC, nu după urcare. Planșa are câțiva
        // megaocteți, iar urcarea ei ținea secunde bune în care fereastra
        // stătea deschisă degeaba: «the box with the progress bar stays open
        // for a bit before it closes. It should close immediately after
        // pressing on generează» (13.10.2026). Cererea pleacă nelegată de
        // ecran, iar dacă pică — un cod luat, rețeaua — o spune bara, acolo
        // unde stau de-acum toate veștile despre citire.
        laPornit(cerut, (projectId
          ? api.pornesteImportDinImagine(projectId, cerut, original ?? imagine.url,
                                         rolulCategoriei(categorie), calc)
          : api.importDinImagineAlMeu(cerut, original ?? imagine.url,
                                      rolulCategoriei(categorie), undefined, calc)
        ).then(() => undefined));
        return;
      }
      // DOCUMENTUL COPIAT E CIORNA, nu versiunea publicată: cine pornește de la
      // M2 pornește de la M2 așa cum arată pe ecranul lui, cu tot cu ce a mutat
      // ieri și n-a publicat. Serverul îi rescrie `id`-ul cu codul cerut.
      const doc = pornire === "existent" && dinCod
        ? (await (projectId ? api.getTronson(projectId, dinCod, { ciorna: true })
                            : api.getTronsonulMeu(dinCod, { ciorna: true }))).doc
        : tronsonNou(cerut, categorie);
      const r = await (projectId
        ? api.salveazaTronsonCaNou(projectId, cerut, doc,
            pornire === "existent" ? dinCod ?? undefined : undefined)
        : api.tronsonNouAlMeu(cerut, doc));
      laCreat(r.code);
    } catch (e) {
      const m = String((e as Error)?.message ?? e);
      // OPRIRILE LANȚULUI AU NUMELE LOR, fiindcă fiecare cere altceva de la om:
      // o planșă mai mare, un plan mai citeț, sau trasarea cu mâna peste calc.
      const oprire = ["prea_mica", "fara_plan", "refuz", "mort", "eroare_interna",
                      "imagine_lipsa"].find((c) => m.includes(c));
      setEroare(
        oprire ? t(`nou.import_${oprire}` as Cheie)
          : /cod_luat/.test(m) ? t("editor.cod_luat", { cod: cerut })
            : /cod_invalid/.test(m) ? t("editor.cod_invalid") : m);
    } finally {
      if (viu.current) { setLucrez(false); setCitire(null); }
    }
  };

  const gata = pornire === "gol"
    || (pornire === "existent" && !!dinCod)
    || (pornire === "imagine" && !!imagine);

  return (
    <div className="ob-tr-nou-fundal" onClick={laInchidere}>
      <div className={`ob-tr-nou card${decupez ? " ob-tr-nou-lat" : ""}`}
           role="dialog" aria-modal="true"
           aria-label={t("nou.titlu")}
           onClick={(e) => e.stopPropagation()}>
        {decupez && fisier ? (
          <>
            <div className="ob-tr-nou-cap">
              <b>{t("nou.decupaj_titlu")}</b>
              <span className="ob-tr-nou-sub">{t("nou.decupaj_sub")}</span>
            </div>
            <DecupajImagine fisier={fisier} laGata={iaDecupajul}
                            laRenunt={() => {
                              setDecupez(false);
                              // Renunțat la prima decupare = nicio imagine aleasă.
                              if (!imagine) setFisier(null);
                            }} />
          </>
        ) : (<>
        <div className="ob-tr-nou-cap">
          <b>{t("nou.titlu")}</b>
          <span className="ob-tr-nou-sub">
            {pas === "forma" ? t("nou.subtitlu") : t("nou.pornire_sub")}
          </span>
        </div>

        {pas === "forma" ? (
          <>
            <div className="ob-tr-nou-feluri">
              {CATEGORII.map((c) => (
                <button key={c.cheie} type="button"
                        className={`ob-tr-nou-fel${categorie === c.cheie ? " on" : ""}`}
                        onClick={() => { setCategorie(c.cheie); setDinCod(null); }}>
                  <Amprenta categorie={c.cheie} scara={3.2} />
                  <b>{t(c.titlu)}</b>
                  <span>{t(c.sub)}</span>
                </button>
              ))}
            </div>

            <label className="ob-tr-nou-cod">
              <span className="ob-lbl">{t("nou.cod")}</span>
              {/* PLAFONUL E AL SERVERULUI, nu unul scris aici. Era 16, iar
                  regula care spune ce se primește (`api.NUME_MODUL`, 60) îl
                  depășea de mult: câmpul tăia tastele la 16 înainte ca
                  validarea să apuce să vadă ceva, deci un titlu lung se putea
                  pune la REDENUMIRE și nu se putea pune la naștere — «in the
                  initial naming box it still presents a small length limit»
                  (19.10.2026). Două plafoane pentru același nume, dintre care
                  unul inventat. Rămâne cel împărțit cu control plane-ul, pe
                  care `pages/redenumire.test` îl ține în pereche cu Python-ul. */}
              <input className="inp ob-majuscule" value={cod} autoFocus
                     placeholder="M9" maxLength={api.LUNGIME_COD}
                     onChange={(e) => setCod(e.target.value)}
                     onKeyDown={(e) => {
                       if (e.key === "Enter" && codOk) setPas("pornire");
                     }} />
            </label>
          </>
        ) : (
          <>
            <div className="ob-tr-nou-cap">
              <b className="ob-tr-nou-intrebare">{t("nou.pornire_titlu")}</b>
            </div>

            <div className="ob-tr-nou-feluri ob-tr-nou-porniri">
              {PORNIRI.map((p) => (
                <button key={p.cheie} type="button"
                        className={`ob-tr-nou-fel${pornire === p.cheie ? " on" : ""}`}
                        onClick={() => { setPornire(p.cheie); setEroare(""); }}>
                  <b>{t(p.titlu)}</b>
                  <span>{t(p.sub)}</span>
                </button>
              ))}
            </div>

            {pornire === "gol" && (
              <p className="ob-tr-nou-nota">{t("nou.nota")}</p>
            )}

            {pornire === "existent" && (
              <div className="ob-tr-nou-lista">
                <span className="ob-lbl">{t("nou.alege_modulul")}</span>
                {module === null && <p className="note">{t("comun.se_incarca")}</p>}
                {module !== null && !deCopiat.length && (
                  <p className="ob-tr-nou-nota">{t("nou.niciun_modul")}</p>
                )}
                {deCopiat.map((m) => (
                  <button key={m.code} type="button"
                          className={`ob-tr-nou-rand${dinCod === m.code ? " on" : ""}`}
                          onClick={() => { setDinCod(m.code); setEroare(""); }}>
                    <b>{m.code}</b>
                    <span>{m.alpha.label ?? ""}</span>
                    <i>{t("nou.modul_cifre", {
                      arie: Math.round(
                        m.alpha.variants.find((v) => v.default)?.useful_m2
                        ?? m.alpha.variants[0]?.useful_m2 ?? 0),
                      flats: m.alpha.flats,
                    })}</i>
                  </button>
                ))}
              </div>
            )}

            {pornire === "imagine" && (
              <div className="ob-tr-nou-poza">
                <input ref={alegeFisier} type="file"
                       accept="image/*,application/pdf"
                       style={{ display: "none" }}
                       onChange={(e) => {
                         alegeImaginea(e.target.files?.[0]);
                         e.target.value = "";
                       }} />
                {imagine
                  ? <img src={imagine.url} alt="" className="ob-tr-nou-previzualizare" />
                  : <div className="ob-tr-nou-gol" aria-hidden="true" />}
                <div className="ob-tr-nou-poza-butoane">
                  <button type="button" className="btn" disabled={lucrez || randez}
                          onClick={() => alegeFisier.current?.click()}>
                    {t(randez ? "nou.pdf_randez"
                              : imagine ? "nou.schimba_imaginea"
                                        : "nou.alege_imaginea")}
                  </button>
                  {fisier && imagine && (
                    <button type="button" className="btn" disabled={lucrez}
                            onClick={() => setDecupez(true)}>
                      {t("nou.decupeaza_iar")}
                    </button>
                  )}
                </div>
              </div>
            )}
          </>
        )}

        {citire && (() => {
          const p = Math.round(procentCitire((acum - citire.pornit) / 1000, citire.stare));
          return (
            <div className="ob-tr-nou-bara" role="progressbar" aria-valuemin={0}
                 aria-valuemax={100} aria-valuenow={p}
                 aria-label={t("nou.se_citeste")}>
              <div className="ob-tr-nou-bara-cap">
                <span>{t("nou.se_citeste")}</span><b>{p} %</b>
              </div>
              <div className="ob-tr-nou-bara-sina">
                <div className="ob-tr-nou-bara-plin" style={{ width: `${p}%` }} />
              </div>
              {pasulCitirii(t, citire.etapa) && (
                <div className="ob-tr-nou-bara-pas">{pasulCitirii(t, citire.etapa)}</div>
              )}
            </div>
          );
        })()}

        {eroare && <div className="err">{eroare}</div>}

        <div className="ob-tr-nou-jos">
          {pas === "pornire" && (
            <button type="button" className="btn ob-tr-nou-inapoi"
                    onClick={() => setPas("forma")}>
              {t("comun.inapoi")}
            </button>
          )}
          <button type="button" className="btn" onClick={laInchidere}>
            {t("comun.renunta")}
          </button>
          {pas === "forma" ? (
            <button type="button" className="btn btnp"
                    disabled={!codOk} onClick={() => setPas("pornire")}>
              {t("comun.mai_departe")}
            </button>
          ) : (
            <button type="button" className="btn btnp"
                    disabled={!codOk || !gata || lucrez}
                    onClick={() => void creeaza()}>
              {lucrez ? "…" : t("comun.creeaza")}
            </button>
          )}
        </div>
        </>)}
      </div>
    </div>
  );
}
